import type { AccessSummary } from './billing';
import { API_BASE_URL } from './api';
import { getAccessToken } from './auth';

const ACCOUNT_OVERVIEW_URL = `${API_BASE_URL}/me/overview`;

export type AccountContentType = 'STATIC' | 'MOTION';
export type AccountExportEventType = 'png' | 'mp4' | 'browser_session';

export interface AccountTemplateSummary {
  id: string;
  name: string;
  templateType: string;
  contentType: AccountContentType;
  preview: string;
  sourceFilter: string;
  domainCategory: string;
  collectionId: string;
  isVideo: boolean;
}

export interface AccountExportSummary {
  templateId: string;
  templateName: string;
  templateType: string;
  contentType: AccountContentType;
  exportType: AccountExportEventType;
  format: string;
  resolution: string;
  createdAt: string;
}

export interface AccountStatsSummary {
  totalExports: number;
  pngDownloads: number;
  mp4Downloads: number;
  browserSessions: number;
  uniqueTemplates: number;
}

export interface AccountOverview {
  favorites: AccountTemplateSummary[];
  recentExports: AccountExportSummary[];
  stats: AccountStatsSummary;
  userId: string;
  publicId: string;
  access: AccessSummary;
}

export async function loadUserAccountOverview(): Promise<AccountOverview> {
  const accessToken = await getAccessToken();
  if (!accessToken) {
    throw new Error('Sign in is required.');
  }

  const response = await fetch(ACCOUNT_OVERVIEW_URL, {
    method: 'GET',
    cache: 'no-store',
    headers: {
      Authorization: `Bearer ${accessToken}`
    }
  });

  if (!response.ok) {
    throw new Error(await readErrorMessage(response, 'Failed to load account overview.'));
  }

  return response.json() as Promise<AccountOverview>;
}

async function readErrorMessage(response: Response, fallback: string): Promise<string> {
  try {
    const payload = (await response.json()) as { error?: string };
    return String(payload?.error || '').trim() || fallback;
  } catch {
    return fallback;
  }
}
